import type { PluginListenerHandle } from '@capacitor/core';

export interface TwoNPlugin {
  // Initialization method
  initialize(options: {
    username: string;  // deviceId
    password: string;  // devicePassword
  }): Promise<{ success: boolean; message: string }>;

  // Anonymous device login - the ONLY supported login method
  loginWithDevice(options: {
    username: string;  // deviceId
    password: string;  // devicePassword
    url?: string;      // Server URL (defaults to https://my2n.com)
  }): Promise<{ success: boolean; message: string; username?: string; deviceId?: string }>;

  // Legacy login method - DEPRECATED, use loginWithDevice instead
  login(options: {
    username: string;
    password: string;
  }): Promise<{ success: boolean; message: string }>;

  isLoggedIn(): Promise<{
    isLoggedIn: boolean;
    state: string;
    deviceId?: string;
    userType?: string;
  }>;

  logout(): Promise<void>;

  startVideoCall(options: {
    deviceId: string;
  }): Promise<void>;

  endVideoCall(): Promise<void>;

  answerIncomingCall(): Promise<{
    success: boolean;
    message: string;
  }>;

  declineIncomingCall(): Promise<{
    success: boolean;
    message: string;
  }>;

  unlockDoor(options: {
    deviceId: string;
  }): Promise<void>;

  // New methods for DTMF-based door unlocking
  getDeviceDTMFCodes(options: {
    deviceId: string;
  }): Promise<{
    dtmfCodes: DTMFCode[];
  }>;

  sendDTMF(options: {
    dtmfCode: string;
    name?: string;
  }): Promise<{
    success: boolean;
    message: string;
  }>;

  toggleAudioOutput(): Promise<{
    currentOutput: string;
  }>;

  toggleMicrophone(options: {
    enabled: boolean;
  }): Promise<void>;

  setupVideoView(options: {
    viewId: string;
  }): Promise<{ 
    success: boolean; 
    message: string;
  }>;

  setupVideoContainer(options: {
    viewId: string;
  }): Promise<{ 
    success: boolean; 
    message: string;
  }>;

  cleanupVideoView(): Promise<{
    success: boolean;
    message: string;
  }>;
  
  // Bluetooth Mobile Key methods
  setupBluetoothManager(options: {
    cardModeEnabled?: boolean;
    cardModeThreshold?: number;
    useSensors?: boolean;
    securityLevel?: 'low' | 'default' | 'high';
    debugNotifications?: boolean;
    debugVibration?: boolean;
  }): Promise<{
    success: boolean;
    message: string;
  }>;
  
  getBleDevices(): Promise<{
    devices: BleDevice[];
  }>;
  
  setDeviceActive(options: {
    uuid: string;
    active: boolean;
  }): Promise<{
    success: boolean;
    message: string;
  }>;
  
  pairDevice(options: {
    uuid: string;
    pin: string;
  }): Promise<{
    success: boolean;
    message: string;
  }>;
  
  unlockDoorWithBle(options: {
    uuid: string;
  }): Promise<{
    success: boolean;
    message: string;
  }>;
  
  enterPinForBle(options: {
    uuid: string;
    pin: string;
  }): Promise<{
    success: boolean;
    message: string;
  }>;
  
  cancelBleOperation(options: {
    uuid: string;
  }): Promise<{
    success: boolean;
    message: string;
  }>;
  
  disconnectBleDevice(options: {
    uuid: string;
  }): Promise<{
    success: boolean;
    message: string;
  }>;
  
  getAccessLogs(): Promise<{
    logs: AccessLogRecord[];
  }>;
  
  importCredentials(options: {
    locations: LocationCredential[];
  }): Promise<{
    success: boolean;
    message: string;
    count: number;
  }>;
  
  clearAllBleData(): Promise<{
    success: boolean;
    message: string;
  }>;
  
  requestLocationPermission(): Promise<{
    status: string;
  }>;
  
  checkBluetoothStatus(): Promise<{
    available: boolean;
    status: string;
  }>;
  
  startBleScanning(): Promise<{
    success: boolean;
    message: string;
    devicesInDatabase: number;
    locationsInDatabase: number;
  }>;
  
  addListener(eventName: 'twoNCallEvent', listenerFunc: (event: CallEvent) => void): Promise<PluginListenerHandle> & PluginListenerHandle;
  addListener(eventName: 'twoNIncomingCall', listenerFunc: (event: IncomingCallEvent) => void): Promise<PluginListenerHandle> & PluginListenerHandle;
  addListener(eventName: 'twoNNotificationReceived', listenerFunc: (event: TwoNNotificationEvent) => void): Promise<PluginListenerHandle> & PluginListenerHandle;
  addListener(eventName: 'twoNLoginInvalid', listenerFunc: (event: any) => void): Promise<PluginListenerHandle> & PluginListenerHandle;
  addListener(eventName: 'twoNCallAutoAnswered', listenerFunc: (event: TwoNNotificationEvent) => void): Promise<PluginListenerHandle> & PluginListenerHandle;
  addListener(eventName: 'twoNNavigateToIntercom', listenerFunc: (event: TwoNNotificationEvent) => void): Promise<PluginListenerHandle> & PluginListenerHandle;
  // BLE events
  addListener(eventName: 'bleDeviceScanned', listenerFunc: (event: BleDeviceScannedEvent) => void): Promise<PluginListenerHandle> & PluginListenerHandle;
  addListener(eventName: 'bleDevicePaired', listenerFunc: (event: BleDeviceEvent) => void): Promise<PluginListenerHandle> & PluginListenerHandle;
  addListener(eventName: 'bleAuthEvent', listenerFunc: (event: BleAuthEvent) => void): Promise<PluginListenerHandle> & PluginListenerHandle;
  addListener(eventName: 'bleDeviceDisconnected', listenerFunc: (event: BleDeviceEvent) => void): Promise<PluginListenerHandle> & PluginListenerHandle;
  addListener(eventName: 'bleCredentialRefreshNeeded', listenerFunc: (event: BleCredentialRefreshEvent) => void): Promise<PluginListenerHandle> & PluginListenerHandle;
  removeAllListeners(): Promise<void>;

  // New preferred method for saving BLE credentials (hex or Base64 input supported)
  saveBleCredentials(options: {
    credentials: LocationCredential[];
  }): Promise<{
    success: boolean;
    saved: string[]; // array of location IDs successfully stored
  }>;

  enableBleCardMode(): Promise<{ success: boolean; message: string; cardMode: { enabled: boolean; threshold: number } }>;
  disableBleCardMode(): Promise<{ success: boolean; message: string; cardMode: { enabled: boolean; threshold: number } }>;
  getBleCardModeStatus(): Promise<{ success: boolean; cardMode: { enabled: boolean; threshold: number } }>;
  
  // Testing method to force update existing credentials with hardcoded AuthId
  forceUpdateCredentials(): Promise<{
    success: boolean;
    message: string;
    updatedLocations: number;
  }>;

  repairNotificationSystem(): Promise<{
    success: boolean;
    message: string;
    diagnostics: Record<string, any>;
  }>;
  
  diagnoseNotificationSystem(): Promise<{
    success: boolean;
    diagnostics: Record<string, any>;
  }>;
}

export interface CallEvent {
  type: 'incoming' | 'connected' | 'disconnected' | 'error' | 'answered' | 'declined' | 'ended';
  message?: string;
}

export interface IncomingCallEvent {
  type: 'incoming';
  message: string;
  timestamp: number;
  readableState: string;
  callState: number;
  callerInfo: Record<string, any>;
  hasVideo: boolean;
}

export interface TwoNNotificationEvent {
  type: string;
  timestamp: number;
  deviceId?: string;
  action?: string;
  reason?: string;
}

// BLE interfaces
export interface BleDevice {
  uuid: string;
  name: string;
  location: string;
  isActivated: boolean;
  minRssi: number;
  maxRssi: number;
  useDynamicThreshold: boolean;
  isCardModeActivated: boolean;
  isTouchModeActivated: boolean;
  isReliable: boolean;
}

export interface BleDeviceScannedEvent {
  uuid: string;
  name: string;
  rssi: number;
  mode: string;
  connectionState: string;
  activated: boolean;
  location: string;
}

export interface BleDeviceEvent {
  uuid: string;
  success?: boolean;
  reason?: string;
}

export interface BleAuthEvent {
  uuid: string;
  event: string;
  reason?: string;
  credential?: string;
  deviceId?: string;
}

export interface BleCredentialRefreshEvent {
  uuid: string;
  location: string;
  reason: string;
}

export interface AccessLogRecord {
  identifier: string;
  deviceUuid: string;
  deviceName: string;
  timestamp: number;
  isSuccess: boolean;
  isCorrectResult: boolean;
}

export interface LocationCredential {
  location: string;
  keyId: string;  // Base64 encoded
  pubKey: string; // Base64 encoded
  authId: string; // Base64 encoded
  authIdHash?: string; // Optional hash for validation
}

// New interface for DTMF codes
export interface DTMFCode {
  code: string;
  name: string;
  description?: string;
}
